import { IParsePostingResult, ISaveJobPostDetailPayload } from '~/domain/PermanentJobs/types';
import { PayType } from '~/domain/PermanentJobs/enums';
import { EnableDailyPayOption } from '~/constants/ff.posthog';
import { PosthogService } from '~/services/posthog';
import { plainTextToHtml } from '~/commonComponents/RichTextEditor';
import { isExcludedProfession } from '~/components/PermanentJobsV2/Constants';

/**
 * Maps a parsed posting (read-only `POST /postings/parse` result) into the
 * draft-update payload persisted via `saveJobPostDetail`.
 */
export class ParsedPostingMapper {
  /**
   * Converts a parse result into a `saveJobPostDetail` payload.
   *
   * Fields the parser could not extract come through as `null`; the Confirm Job
   * Details screen re-reads the saved draft and maps it to the form via
   * `JobDetailsFormMapper.fromEntity`, so those gaps surface as blank inputs.
   *
   * Office fields (`officeId` / `officeSelected`) are intentionally omitted —
   * the parser never returns office data, so office selection follows the same
   * logic as the manual flow (resolved at draft creation / on the Confirm page).
   *
   * `expectedDays` already share the form's 0=Sunday … 6=Saturday indexing, so
   * they pass through unchanged. Empty `benefits` / `expectedDays` arrays map to
   * `null` to mirror the "unset means null" convention `JobDetailsFormMapper`
   * uses. A parsed `Daily` pay type is dropped to `null` when the
   * `EnableDailyPayOption` flag is off, so an unsupported value is never saved.
   * A parsed `professionType` / `subProfession` the flow excludes
   * (`isExcludedProfession`, the same rule that hides those professions from the
   * form's dropdowns) is unconditionally dropped to `null`, so the office is
   * asked to pick a supported profession instead of publishing one the
   * permanent-jobs flow does not support.
   *
   * The parser returns `description` as plain text with `\n` line breaks; it is
   * converted to HTML (`plainTextToHtml`) before saving so the Quill-based
   * RichTextEditor on the Confirm Job Details page preserves its paragraph
   * structure instead of collapsing it into one run-on block.
   */
  static toSaveJobPostDetailPayload(
    parsed: IParsePostingResult,
  ): Partial<ISaveJobPostDetailPayload> {
    const { data } = parsed;

    const isDailyPayEnabled = PosthogService.getInstance().isFeatureEnabled(EnableDailyPayOption);
    const payType =
      data.payType === PayType.DAILY && !isDailyPayEnabled ? null : data.payType;

    const professionType = isExcludedProfession(data.professionType) ? null : data.professionType;
    const subProfession = isExcludedProfession(data.subProfession) ? null : data.subProfession;

    return {
      title: data.title,
      professionType,
      subProfession,
      officeSpecialty: data.officeSpecialty,
      payType,
      minRate: data.minRate,
      maxRate: data.maxRate,
      benefits: data.benefits.length ? data.benefits : null,
      benefitsOther: data.benefitsOther,
      scheduleType: data.scheduleType,
      expectedDays: data.expectedDays?.length ? data.expectedDays : null,
      description: plainTextToHtml(data.description),
    };
  }
}
